import React, { useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Store, Check, X } from "lucide-react";

/**
 * Multi-select "Sede" filter. Shows a compact button that opens a popover with
 * a checkbox list of every branch. Emits an array of branch ids via `onChange`.
 *
 * Empty array (or null) = "Tutte le sedi" (nessun filtro).
 *
 * Props:
 *  - branches: [{ id, name, address? }]  → i.e. `GET /branches` payload
 *  - value: string[]                     → array of currently-selected branch ids
 *  - onChange: (ids: string[]) => void
 *  - label?: string                      → shown as small header ("Sede" default)
 *  - placeholder?: string                → "Tutte le sedi" default
 *  - testId?: string                     → data-testid prefix for the trigger
 *  - compact?: boolean                   → compact height (h-8) for dense toolbars
 */
export default function BranchMultiSelect({
  branches = [],
  value = [],
  onChange,
  label = "Sede",
  placeholder = "Tutte le sedi",
  testId = "branch-multiselect",
  compact = false,
  align = "start",
}) {
  const selected = Array.isArray(value) ? value : [];
  const summary = useMemo(() => {
    if (!selected.length) return placeholder;
    if (selected.length === 1) {
      const b = branches.find((x) => x.id === selected[0]);
      return b?.name || "1 sede";
    }
    if (selected.length === branches.length) return "Tutte le sedi";
    return `${selected.length} sedi selezionate`;
  }, [selected, branches, placeholder]);

  const toggle = (id) => {
    if (selected.includes(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };
  const clearAll = () => onChange([]);
  const selectAll = () => onChange(branches.map((b) => b.id));

  const btnHeight = compact ? "h-8" : "h-10";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`w-full ${btnHeight} px-3 rounded-md bg-zinc-950 border border-zinc-800 text-left text-sm flex items-center justify-between gap-2 hover:border-zinc-700`}
        >
          <span className="flex items-center gap-2 truncate">
            <Store className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
            <span className={`truncate ${selected.length ? "text-zinc-100" : "text-zinc-500"}`}>{summary}</span>
          </span>
          <span className="flex items-center gap-1">
            {selected.length > 0 && (
              <span
                data-testid={`${testId}-clear`}
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); clearAll(); }}
                onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); clearAll(); } }}
                className="p-0.5 rounded hover:bg-zinc-800 text-zinc-400 cursor-pointer"
                title="Azzera selezione"
              >
                <X className="h-3.5 w-3.5" />
              </span>
            )}
            <span className="text-zinc-500 text-xs">▾</span>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 bg-zinc-950 border-zinc-800 p-2" align={align}>
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold px-1 pb-2 flex items-center justify-between">
          <span>{label} · seleziona una o più</span>
          <div className="flex gap-2 normal-case tracking-normal">
            <button
              type="button"
              data-testid={`${testId}-select-all`}
              onClick={selectAll}
              className="text-zinc-400 hover:text-zinc-100"
            >Tutte</button>
            <button
              type="button"
              data-testid={`${testId}-deselect`}
              onClick={clearAll}
              className="text-zinc-400 hover:text-zinc-100"
            >Nessuna</button>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {branches.length === 0 && (
            <div className="text-xs text-zinc-500 px-2 py-3">Nessuna sede configurata.</div>
          )}
          {branches.map((b) => {
            const on = selected.includes(b.id);
            return (
              <button
                key={b.id}
                type="button"
                data-testid={`${testId}-option-${b.id}`}
                onClick={() => toggle(b.id)}
                className={`w-full text-left px-2 py-2 rounded-md text-sm flex items-center gap-2 hover:bg-zinc-800 ${on ? "text-amber-300" : "text-zinc-300"}`}
              >
                <span className={`h-4 w-4 rounded border flex items-center justify-center flex-shrink-0 ${on ? "bg-amber-500/20 border-amber-500 text-amber-300" : "border-zinc-700"}`}>
                  {on && <Check className="h-3 w-3" />}
                </span>
                <span className="truncate">{b.name}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
